import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';

const Timeline = ({ searchTerm = '', onSearchResults, navigateRef, onDataChange }) => {
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMatch, setCurrentMatch] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const timelineRef = useRef(null);
  const currentMatchRef = useRef(0);

  // Add this at the top of the component
  const isMobileOrTablet = () => window.innerWidth <= 1164;

  // Fetch data from API
  useEffect(() => {
    fetchTimelineData();
  }, []);

  const fetchTimelineData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/timeline-data');
      const data = await response.json();
      
      setTimelineData(data);
      if (onDataChange) onDataChange(data);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Notify parent component of data changes
  useEffect(() => {
    if (onDataChange) {
      onDataChange(timelineData);
    }
  }, [onDataChange]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (regex.test(part)) {
        return <mark key={index} className="search-highlight">{part}</mark>;
      }
      return part;
    });
  };

  // Filter all data (no date range filtering)
  const filteredData = timelineData;

  // Count total matches when search term changes
  useEffect(() => {
    if (searchTerm) {
      let totalMatches = 0;
      filteredData.forEach(item => {
        // Include participant data in search
        let searchText = `${item.title} ${item.subtitle} ${item.description} ${item.date}`;
        
        // Add participant information to search text
        if (item.participants && item.participants.length > 0) {
          const participantText = item.participants.map(participant => 
            `${participant.name} ${participant.roles?.[0] || ''} ${participant.affiliations || ''} ${participant.countries || ''}`
          ).join(' ');
          searchText += ` ${participantText}`;
        }
        
        const matches = searchText.toLowerCase().match(new RegExp(searchTerm.toLowerCase(), 'g'));
        if (matches) {
          totalMatches += matches.length;
        }
      });
      
      setTotalMatches(totalMatches);
      
      // Only reset to 1 if current match is 0 or invalid
      setCurrentMatch(prevMatch => {
        if (prevMatch === 0 || prevMatch > totalMatches) {
          const newMatch = totalMatches > 0 ? 1 : 0;
          currentMatchRef.current = newMatch;
          return newMatch;
        }
        // Keep current match if it's still valid
        currentMatchRef.current = prevMatch;
        return prevMatch;
      });
    } else {
      setTotalMatches(0);
      setCurrentMatch(0);
      currentMatchRef.current = 0;
    }
  }, [searchTerm, filteredData]);

  // Update parent with search results
  useEffect(() => {
    if (onSearchResults) {
      onSearchResults({ total: totalMatches, current: currentMatch });
    }
  }, [totalMatches, currentMatch, onSearchResults]);

  // Navigate to specific match
  const navigateToMatch = useCallback((matchNumber) => {
    if (matchNumber < 1 || matchNumber > totalMatches) return;
    
    console.log(`Navigating to match ${matchNumber} (array index ${matchNumber - 1})`);
    setCurrentMatch(matchNumber);
    currentMatchRef.current = matchNumber;
    
    // Wait for DOM to update, then scroll
    setTimeout(() => {
      const highlights = timelineRef.current?.querySelectorAll('.search-highlight');
      console.log(`Found ${highlights?.length || 0} highlights, looking for match ${matchNumber} (index ${matchNumber - 1})`);
      
      if (highlights && highlights[matchNumber - 1]) {
        // Remove current class from all highlights
        highlights.forEach(highlight => highlight.classList.remove('current-match'));
        // Add current class to target highlight
        highlights[matchNumber - 1].classList.add('current-match');
        // Scroll to it
        console.log(`Scrolling to highlight at index ${matchNumber - 1}`);
        highlights[matchNumber - 1].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      } else {
        console.log(`Highlight at index ${matchNumber - 1} not found`);
      }
    }, 100);
  }, [totalMatches]);

  // Handle initial scroll to first match when search starts
  useEffect(() => {
    if (searchTerm && currentMatch === 1 && totalMatches > 0) {
      // Add a delay to ensure DOM is fully updated
      const timeoutId = setTimeout(() => {
        const highlights = timelineRef.current?.querySelectorAll('.search-highlight');
        if (highlights && highlights.length > 0) {
          console.log(`Found ${highlights.length} highlights, scrolling to first one (index 0)`);
          highlights.forEach(highlight => highlight.classList.remove('current-match'));
          highlights[0].classList.add('current-match');
          highlights[0].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        } else {
          console.log('No highlights found for initial scroll');
        }
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, currentMatch, totalMatches]);

  const handleNavigate = useCallback((direction) => {
    if (totalMatches === 0) return;
    
    const currentMatchValue = currentMatchRef.current;
    console.log(`Current match: ${currentMatchValue}, Total: ${totalMatches}, Direction: ${direction}`);
    
    let newMatch = currentMatchValue;
    if (direction === 'next') {
      newMatch = currentMatchValue >= totalMatches ? 1 : currentMatchValue + 1;
    } else if (direction === 'previous') {
      newMatch = currentMatchValue <= 1 ? totalMatches : currentMatchValue - 1;
    }
    
    console.log(`Navigating from ${currentMatchValue} to ${newMatch}`);
    navigateToMatch(newMatch);
  }, [totalMatches, navigateToMatch]);

  // Set navigation function in ref
  useEffect(() => {
    if (navigateRef) {
      navigateRef.current = handleNavigate;
    }
  }, [handleNavigate, navigateRef]);

  return (
    <div ref={timelineRef}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Loading timeline data...
        </div>
      ) : (
        <VerticalTimeline>
          {filteredData.map((item, index) => (
            <VerticalTimelineElement
              key={item.id}
              className={item.className}
              contentStyle={{ background: '#00313D', color: '#fff' }}
              contentArrowStyle={isMobileOrTablet() ? {} : { borderRight: '7px solid #00313D' }}
              iconStyle={{ background: '#00313D', color: '#fff' }}
            >
              <div className="timeline-date">{highlightText(formatDate(item.date), searchTerm)}</div>
              <h3 className="vertical-timeline-element-title">{highlightText(item.title, searchTerm)}</h3>
              <h5 className="vertical-timeline-element-subtitle location-text">Location: {highlightText(item.subtitle, searchTerm)}</h5>
              <p>{highlightText(item.description, searchTerm)}</p>
              
              {/* Participants Section */}
              {item.participants && item.participants.length > 0 && (
                <div className="participants-section">
                  <h5 className="participants-section-title">Participants ({String(item.participants_count)})</h5>
                  <div className="participants-list">
                    {item.participants.map((participant, idx) => (
                      <div key={idx} className="participant-item">
                        <div className="participant-name">
                          {highlightText(participant.name, searchTerm)}
                        </div>
                        <div className="participant-details">
                          <span className="participant-role">
                            {highlightText(String(participant.roles?.[0] || 'No role'), searchTerm)}
                          </span>
                          <span className="participant-org">
                            {highlightText(` • ${participant.affiliations?.[0] || 'No organization'}`, searchTerm)}
                          </span>
                            <span className="participant-country">
                              {highlightText(` • ${participant.countries?.[0] || 'No country'}`, searchTerm)}
                            </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
          
            </VerticalTimelineElement>
          ))}
        </VerticalTimeline>
      )}
    </div>
  );
};

export default Timeline;
